export const useRequestSecurity = () => {
  const generateNonce = (): string => {
    const array = new Uint8Array(16)
    crypto.getRandomValues(array)
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('')
  }

  const getSessionSecret = (): string | null => {
    if (process.client) {
      return useSessionStorage<string | null>('security_secret', null).value
    }
    return null
  }

  const setSessionSecret = (secret: string | null): void => {
    if (process.client) {
      const storage = useSessionStorage<string | null>('security_secret', null)
      storage.value = secret
    }
  }

  const generateRequestSignature = async (payload: Record<string, any>, timestamp: number, nonce: string, sessionSecret?: string, firebaseToken?: string): Promise<string> => {
    try {
      const config = useRuntimeConfig()
      const apiUrl = config.public.API_URL || ''
      
      if (!apiUrl) {
        throw new Error('API_URL is not configured')
      }

      const secretKey = sessionSecret || getSessionSecret() || firebaseToken
      if (!secretKey) {
        throw new Error('Session secret or Firebase token is not available')
      }

      const sortedKeys = Object.keys(payload).filter(key => key !== 'request_id').sort()
      const sortedPayload: Record<string, any> = {}
      sortedKeys.forEach(key => {
        sortedPayload[key] = payload[key]
      })

      const payloadString = new URLSearchParams(sortedPayload).toString()
      const secret = `${apiUrl}|${secretKey}`
      const message = `${payloadString}|${timestamp}|${nonce}`

      const encoder = new TextEncoder()
      const keyData = encoder.encode(secret)
      const messageData = encoder.encode(message)

      const cryptoKey = await crypto.subtle.importKey(
        'raw',
        keyData,
        { name: 'HMAC', hash: 'SHA-256' },
        false,
        ['sign']
      )

      const signature = await crypto.subtle.sign('HMAC', cryptoKey, messageData)
      const hashArray = Array.from(new Uint8Array(signature))
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
      
      return hashHex
    } catch (error) {
      console.error('Signature generation error:', error)
      throw error
    }
  }

  const generateResponseSignature = async (content: string, timestamp: number, nonce: string, sessionSecret?: string): Promise<string> => {
    try {
      const config = useRuntimeConfig()
      const apiUrl = config.public.API_URL || ''
      
      if (!apiUrl) {
        throw new Error('API_URL is not configured')
      }

      const secretKey = sessionSecret || getSessionSecret()
      if (!secretKey) {
        throw new Error('Session secret is not available')
      }

      const secret = `${apiUrl}|${secretKey}`
      const message = `${content}|${timestamp}|${nonce}`

      const encoder = new TextEncoder()
      const keyData = encoder.encode(secret)
      const messageData = encoder.encode(message)

      const cryptoKey = await crypto.subtle.importKey(
        'raw',
        keyData,
        { name: 'HMAC', hash: 'SHA-256' },
        false,
        ['sign']
      )

      const signature = await crypto.subtle.sign('HMAC', cryptoKey, messageData)
      const hashArray = Array.from(new Uint8Array(signature))
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
      
      return hashHex
    } catch (error) {
      console.error('Response signature generation error:', error)
      throw error
    }
  }

  const generateRequestId = (): string => {
    return crypto.randomUUID()
  }

  const signRequest = async (payload: Record<string, any>, sessionSecret?: string, firebaseToken?: string): Promise<Record<string, any>> => {
    const timestamp = Math.floor(Date.now() / 1000)
    const nonce = generateNonce()
    
    try {
      const signature = await generateRequestSignature(payload, timestamp, nonce, sessionSecret, firebaseToken)
      
      return {
        ...payload,
        timestamp,
        nonce,
        signature
      }
    } catch (error) {
      console.error('Failed to sign request:', error)
      throw error
    }
  }

  const verifyResponse = async (content: string, signature: string, timestamp: string, nonce: string, sessionSecret?: string): Promise<boolean> => {
    try {
      const timestampNum = parseInt(timestamp, 10)
      const currentTime = Math.floor(Date.now() / 1000)
      const tolerance = 300

      if (Math.abs(currentTime - timestampNum) > tolerance) {
        console.error('Response timestamp out of tolerance')
        return false
      }

      const expectedSignature = await generateResponseSignature(content, timestampNum, nonce, sessionSecret)
      
      return expectedSignature === signature
    } catch (error) {
      console.error('Response verification error:', error)
      return false
    }
  }

  const validateHttps = (): boolean => {
    if (process.client) {
      const isLocal = window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1' ||
                     window.location.hostname.startsWith('192.168.') ||
                     window.location.hostname.startsWith('10.')
      
      const isSecure = window.location.protocol === 'https:'
      const isSecureContext = window.isSecureContext === true
      
      if (!isLocal && (!isSecure || !isSecureContext)) {
        console.error('HTTPS is required for secure requests')
        throw new Error('SECURITY_HTTPS_REQUIRED: HTTPS is required for secure requests')
      }
    }
    
    return true
  }

  const shouldSkipSigning = (url: string, method: string): boolean => {
    const skipPaths = [
      '/api/health',
      '/api/audit',
      '/api/webhook/brevo',
      '/api/webhook-test',
      '/sanctum/csrf-cookie',
      '/api/firebase/authenticate',
      '/api/login',
      '/api/register',
    ]

    for (const skipPath of skipPaths) {
      if (url.includes(skipPath)) {
        return true
      }
    }

    if (method.toLowerCase() === 'get') {
      return true
    }

    const isMfaEndpoint = /\/api\/(mfa|mfa-login)\//.test(url)
    if (isMfaEndpoint) {
      return false
    }

    const sessionSecret = getSessionSecret()
    if (!sessionSecret) {
      return true
    }

    return false
  }

  return {
    signRequest,
    verifyResponse,
    validateHttps,
    generateNonce,
    generateRequestId,
    shouldSkipSigning,
    getSessionSecret,
    setSessionSecret
  }
}
